import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { cancelOrder, fetchOrder, PaymentStatus, rateOrder } from '../api/orders';
import { StatusBadge } from '../components/StatusBadge';
import { OrderWorkflowStepper } from '../components/OrderWorkflowStepper';
import { useOrderTracking } from '../hooks/useOrderTracking';
import { ApiError } from '../api/client';
import { Spinner } from '../components/ui/Spinner';
import { formatDateTime } from '../lib/format';
import { Avatar } from '../components/Avatar';

const TERMINAL: Array<string> = ['delivered', 'cancelled', 'failed'];

const PAYMENT_LABELS: Record<PaymentStatus, string> = {
  pending: 'En attente',
  paid: 'Payé',
  failed: 'Échec du paiement',
  refunded: 'Remboursé'
};

export const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['order', id],
    queryFn: () => fetchOrder(id!),
    enabled: !!id
  });

  const order = data?.data;
  const { events, connected } = useOrderTracking(order?.trackingCode);

  const [rating, setRating] = useState<number>(5);
  const [feedback, setFeedback] = useState<string>('');
  const [cancelReason, setCancelReason] = useState<string>('');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['order', id] });
    queryClient.invalidateQueries({ queryKey: ['orders'] });
  };

  const cancelMutation = useMutation({
    mutationFn: (reason?: string) => cancelOrder(id!, reason),
    onSuccess: invalidate
  });

  const rateMutation = useMutation({
    mutationFn: () => rateOrder(id!, rating, feedback || undefined),
    onSuccess: invalidate
  });

  if (isLoading) {
    return (
      <div className="card flex min-h-[240px] flex-col items-center justify-center gap-3 p-8 text-slate-500">
        <Spinner />
        <p className="text-sm">Chargement de la commande…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
        {error instanceof ApiError ? error.message : 'Erreur'}
      </div>
    );
  }
  if (!order) return null;

  const canCancel = !TERMINAL.includes(order.status) && order.status !== 'picked_up' && order.status !== 'in_transit';
  const canRate = order.status === 'delivered' && order.rating == null;
  const delivererUser = order.Deliverer?.User;
  const customer = order.Customer;
  const customerRoleLabel = customer?.role === 'business' ? 'Vendeur' : 'Client';

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <Link
          to="/orders"
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          <span aria-hidden>←</span> Retour aux commandes
        </Link>
      </div>

      <div className="card overflow-hidden p-6 sm:p-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Code de suivi</p>
            <p className="mt-1 font-mono text-xl font-medium text-slate-900 sm:text-2xl">{order.trackingCode}</p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <StatusBadge status={order.status} />
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                connected ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200' : 'bg-slate-100 text-slate-500'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'animate-pulse bg-emerald-500' : 'bg-slate-400'}`} />
              {connected ? 'Suivi temps réel actif' : 'Hors ligne'}
            </span>
          </div>
        </div>

        {(order.Deliverer || customer) && (
          <div className="mt-6 flex flex-wrap gap-6 border-t border-slate-100 pt-6">
            {order.Deliverer && (
              <div className="flex items-center gap-3">
                <Avatar
                  size="md"
                  profilePictureUrl={delivererUser?.profilePictureUrl}
                  firstName={delivererUser?.firstName}
                  lastName={delivererUser?.lastName}
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Livreur</p>
                  <p className="text-sm font-medium text-slate-900">
                    {order.Deliverer.companyName ||
                      [delivererUser?.firstName, delivererUser?.lastName].filter(Boolean).join(' ') ||
                      'Attribué'}
                  </p>
                  {order.Deliverer.vehicleType ? (
                    <p className="text-xs text-slate-500">{order.Deliverer.vehicleType}</p>
                  ) : null}
                </div>
              </div>
            )}
            {customer && (
              <div className="flex items-center gap-3">
                <Avatar
                  size="md"
                  profilePictureUrl={customer.profilePictureUrl}
                  firstName={customer.firstName}
                  lastName={customer.lastName}
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{customerRoleLabel}</p>
                  <p className="text-sm font-medium text-slate-900">
                    {[customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.email}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 border-t border-slate-100 pt-8">
          <h2 className="text-sm font-semibold text-slate-900">Parcours logistique</h2>
          <p className="mt-1 text-xs text-slate-500">Du dépôt à la remise : même fil d’étapes que sur la liste des commandes.</p>
          <div className="mt-6 rounded-xl border border-slate-100 bg-slate-50/40 p-4 sm:p-5">
            <OrderWorkflowStepper status={order.status} />
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
          <section className="rounded-xl border border-slate-100 bg-slate-50/50 p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prise en charge</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-800">{order.pickupAddress}</p>
            <dl className="mt-4 space-y-1 text-xs text-slate-500">
              <div>
                <dt className="inline font-medium text-slate-600">Planifiée :</dt>{' '}
                <dd className="inline">{formatDateTime(order.scheduledPickupTime)}</dd>
              </div>
              <div>
                <dt className="inline font-medium text-slate-600">Réelle :</dt>{' '}
                <dd className="inline">{formatDateTime(order.actualPickupTime)}</dd>
              </div>
            </dl>
          </section>
          <section className="rounded-xl border border-slate-100 bg-slate-50/50 p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Livraison</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-800">{order.deliveryAddress}</p>
            <dl className="mt-4 space-y-1 text-xs text-slate-500">
              <div>
                <dt className="inline font-medium text-slate-600">Livrée :</dt>{' '}
                <dd className="inline">{formatDateTime(order.actualDeliveryTime)}</dd>
              </div>
              <div>
                <dt className="inline font-medium text-slate-600">Destinataire :</dt>{' '}
                <dd className="inline">{order.recipientName}</dd>
              </div>
            </dl>
          </section>
        </div>

        <dl className="mt-10 grid grid-cols-2 gap-4 border-t border-slate-100 pt-8 md:grid-cols-4">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Prix</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-slate-900">
              {order.price.toFixed(2)} {order.currency}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Distance</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums text-slate-900">{order.distance.toFixed(1)} km</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Paiement</dt>
            <dd className="mt-1 text-lg font-semibold text-slate-900">{PAYMENT_LABELS[order.paymentStatus]}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Créée</dt>
            <dd className="mt-1 text-sm font-medium text-slate-800">{formatDateTime(order.createdAt)}</dd>
          </div>
        </dl>
      </div>

      <div className="card p-6 sm:p-8">
        <h3 className="text-sm font-semibold text-slate-900">Événements temps réel</h3>
        <p className="mt-1 text-xs text-slate-500">Historique des changements de statut reçus via WebSocket.</p>
        {events.length === 0 ? (
          <p className="mt-6 text-sm text-slate-500">
            Aucun changement pour l’instant. Les mises à jour apparaîtront ici dès réception.
          </p>
        ) : (
          <ul className="mt-6 divide-y divide-slate-100 rounded-xl border border-slate-100">
            {events.map((e, idx) => (
              <li key={`${e.at}-${idx}`} className="flex items-center justify-between gap-4 px-4 py-3">
                <StatusBadge status={e.status} />
                <time className="text-xs tabular-nums text-slate-500">{formatDateTime(e.at)}</time>
              </li>
            ))}
          </ul>
        )}
      </div>

      {canCancel && (
        <div className="card border-red-100 bg-red-50/40 p-6 sm:p-8">
          <h3 className="text-sm font-semibold text-slate-900">Annuler cette commande</h3>
          <p className="mt-1 text-xs text-slate-600">Cette action est soumise aux règles métier côté serveur.</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              className="input flex-1 bg-white"
              placeholder="Raison (optionnelle)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            <button
              type="button"
              className="btn-danger shrink-0"
              disabled={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate(cancelReason || undefined)}
            >
              {cancelMutation.isPending ? 'Annulation…' : 'Annuler la commande'}
            </button>
          </div>
          {cancelMutation.error && (
            <p className="mt-3 text-sm text-red-600" role="alert">
              {cancelMutation.error instanceof ApiError ? cancelMutation.error.message : 'Erreur'}
            </p>
          )}
        </div>
      )}

      {canRate && (
        <div className="card p-6 sm:p-8">
          <h3 className="text-sm font-semibold text-slate-900">Noter cette livraison</h3>
          <p className="mt-1 text-xs text-slate-500">Votre avis aide à améliorer la qualité du service.</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                className={`flex h-11 w-11 items-center justify-center rounded-full border text-sm font-semibold transition-all ${
                  rating >= n
                    ? 'border-amber-400 bg-amber-400 text-white shadow-sm scale-105'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-amber-300'
                }`}
                onClick={() => setRating(n)}
              >
                {n}
              </button>
            ))}
          </div>
          <textarea
            className="input mt-4"
            rows={3}
            placeholder="Commentaire (optionnel)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <button
            type="button"
            className="btn-primary mt-4"
            disabled={rateMutation.isPending}
            onClick={() => rateMutation.mutate()}
          >
            {rateMutation.isPending ? 'Envoi…' : 'Envoyer l’évaluation'}
          </button>
          {rateMutation.error && (
            <p className="mt-3 text-sm text-red-600" role="alert">
              {rateMutation.error instanceof ApiError ? rateMutation.error.message : 'Erreur'}
            </p>
          )}
          {rateMutation.isSuccess && (
            <p className="mt-3 text-sm font-medium text-emerald-700">Merci pour votre évaluation.</p>
          )}
        </div>
      )}

      {order.rating != null && (
        <div className="card p-6 sm:p-8">
          <h3 className="text-sm font-semibold text-slate-900">Votre évaluation</h3>
          <p className="mt-3 text-2xl tracking-tight" aria-label={`Note ${order.rating} sur 5`}>
            <span className="text-amber-400">{'★'.repeat(order.rating)}</span>
            <span className="text-slate-200">{'★'.repeat(5 - order.rating)}</span>
          </p>
          {order.feedback && <p className="mt-3 text-sm italic text-slate-600">« {order.feedback} »</p>}
        </div>
      )}
    </div>
  );
};
